<template>
  <div v-if="gm" class="tip-dynamics">
    <!-- Header strip: full-width, score centred, meta on the sides -->
    <div class="dyn-header">
      <div class="dyn-side dyn-side-home">
        <div class="dyn-team-name" :title="gm.homeTeam">{{ truncate(gm.homeTeam, 28) }}</div>
        <div class="dyn-team-label">HOME</div>
      </div>
      <div class="dyn-centre">
        <div class="dyn-score">
          <span class="dyn-goal dyn-goal-home">{{ animHomeGoals }}</span>
          <span class="dyn-score-sep">—</span>
          <span class="dyn-goal dyn-goal-away">{{ animAwayGoals }}</span>
        </div>
        <div class="dyn-pills">
          <span class="dyn-pill dyn-pill-minute">{{ minuteText }}</span>
          <span v-if="confidence" class="dyn-pill dyn-pill-conf" :title="`Confidence ${confidence}/5`">
            {{ stars }}
          </span>
        </div>
      </div>
      <div class="dyn-side dyn-side-away">
        <div class="dyn-team-name" :title="gm.awayTeam">{{ truncate(gm.awayTeam, 28) }}</div>
        <div class="dyn-team-label">AWAY</div>
      </div>
    </div>

    <!-- Insight strip: trigger reason + hot side, side-by-side on wide -->
    <div class="dyn-insights">
      <div v-if="triggerReason" class="dyn-insight dyn-insight-trigger">
        <div class="dyn-insight-icon">🎯</div>
        <div>
          <div class="dyn-insight-label">Why this fired</div>
          <div class="dyn-insight-text">{{ triggerReason }}</div>
        </div>
      </div>
      <div v-if="hotSide" class="dyn-insight" :class="hotSide.cls">
        <div class="dyn-insight-icon">🔥</div>
        <div>
          <div class="dyn-insight-label">Match leader</div>
          <div class="dyn-insight-text">{{ hotSide.text }}</div>
        </div>
      </div>
    </div>

    <!-- Head-to-head stat splits: 2 columns on wide, 1 on narrow -->
    <div class="dyn-stats-grid">
      <div v-for="s in statRows" :key="s.label" class="dyn-stat">
        <div class="dyn-stat-head">
          <span class="dyn-stat-label">{{ s.label }}</span>
          <span v-if="s.winner" class="dyn-stat-winner" :class="s.winner === 'home' ? 'is-home' : 'is-away'">
            {{ s.winner === 'home' ? '◀ ' + truncate(gm.homeTeam, 14) : truncate(gm.awayTeam, 14) + ' ▶' }}
          </span>
        </div>
        <!-- Split bar: home grows from left, away grows from right; widths proportional to share -->
        <div class="dyn-split">
          <div class="dyn-split-val dyn-split-val-home">{{ s.home }}{{ s.suffix || '' }}</div>
          <div class="dyn-split-track">
            <div class="dyn-split-fill dyn-split-home" :style="{ width: s.shareHome + '%' }"></div>
            <div class="dyn-split-fill dyn-split-away" :style="{ width: s.shareAway + '%' }"></div>
          </div>
          <div class="dyn-split-val dyn-split-val-away">{{ s.away }}{{ s.suffix || '' }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ["gameMoment", "algorithm", "homeTeam", "awayTeam", "homeGoals", "awayGoals", "minute", "confidence"],
  data() {
    return {
      animHomeGoals: 0,
      animAwayGoals: 0,
    };
  },
  computed: {
    gm() {
      const m = this.gameMoment || {};
      return {
        homeTeam: m.homeTeam || this.homeTeam,
        awayTeam: m.awayTeam || this.awayTeam,
        homeGoals: m.homeGoals ?? this.homeGoals,
        awayGoals: m.awayGoals ?? this.awayGoals,
        minute: m.minute ?? this.minute,
        homePossession: m.homePossession || 50,
        awayPossession: m.awayPossession || 50,
        homeOnTarget: m.homeOnTarget || 0,
        awayOnTarget: m.awayOnTarget || 0,
        homeDangerousAttacks: m.homeDangerousAttacks || 0,
        awayDangerousAttacks: m.awayDangerousAttacks || 0,
        homeAttacks: m.homeAttacks || 0,
        awayAttacks: m.awayAttacks || 0,
        homeCorners: m.homeCorners || 0,
        awayCorners: m.awayCorners || 0,
        homeYellowCards: m.homeYellowCards || 0,
        awayYellowCards: m.awayYellowCards || 0,
        homeRedCards: m.homeRedCards || 0,
        awayRedCards: m.awayRedCards || 0,
      };
    },
    minuteText() {
      const m = this.gm.minute;
      if (m == null) return "FT";
      const n = typeof m === "number" ? m : parseFloat(m);
      if (isNaN(n) || n === 0) return "FT";
      return `${Math.floor(n)}'`;
    },
    stars() {
      const c = parseInt(this.confidence) || 0;
      return "⭐".repeat(Math.max(0, Math.min(5, c)));
    },
    triggerReason() {
      const a = (this.algorithm || "").toLowerCase();
      if (a.includes("latecoast")) return "Late, low-action under-goals window";
      if (a.includes("lateleadunder")) return "Late lead + high-scoring close-out";
      if (a.includes("oneintwelve")) return "Early goal + active match dynamics";
      if (a.includes("breadcrumb")) return "Goal-trending breadcrumb signal";
      if (a.includes("blink")) return "Late-game momentum shift";
      if (a.includes("3-30") || a.includes("3 in 30")) return "30-min goal opportunity window";
      if (a.includes("corner")) return "Corner-rate trigger";
      if (a.includes("goldmine")) return "Late-game high-pressure trigger";
      if (a.includes("comeback")) return "Trailing home side, late surge potential";
      if (a.includes("late under")) return "Late, suppressed-scoring window";
      return null;
    },
    statRows() {
      const g = this.gm;
      const make = (label, home, away, opts = {}) => {
        const sum = home + away;
        let shareHome = sum > 0 ? Math.round((home / sum) * 100) : 50;
        let shareAway = sum > 0 ? 100 - shareHome : 50;
        // Avoid zero-width fills when there IS data (one side dominant) — give a sliver
        if (sum > 0 && home > 0 && shareHome < 4) shareHome = 4;
        if (sum > 0 && away > 0 && shareAway < 4) shareAway = 4;
        let winner = null;
        if (home > away) winner = "home";
        else if (away > home) winner = "away";
        return { label, home, away, suffix: opts.suffix || "", shareHome, shareAway, winner };
      };
      return [
        make("Possession", g.homePossession, g.awayPossession, { suffix: "%" }),
        make("Shots on Target", g.homeOnTarget, g.awayOnTarget),
        make("Dangerous Attacks", g.homeDangerousAttacks, g.awayDangerousAttacks),
        make("Attacks", g.homeAttacks, g.awayAttacks),
        make("Corners", g.homeCorners, g.awayCorners),
        make("Cards", g.homeYellowCards + g.homeRedCards, g.awayYellowCards + g.awayRedCards),
      ];
    },
    hotSide() {
      let home = 0, away = 0;
      for (const r of this.statRows) {
        if (r.winner === "home") home++;
        else if (r.winner === "away") away++;
      }
      if (home === away) return null;
      const winning = home > away ? this.gm.homeTeam : this.gm.awayTeam;
      const score = Math.max(home, away);
      const total = this.statRows.length;
      return {
        text: `${truncateStr(winning, 22)} ahead on ${score}/${total} stats`,
        cls: home > away ? "is-home" : "is-away",
      };
    },
  },
  mounted() {
    this.countUp("animHomeGoals", this.gm.homeGoals ?? 0);
    this.countUp("animAwayGoals", this.gm.awayGoals ?? 0);
  },
  methods: {
    truncate(s, n) {
      return truncateStr(s, n);
    },
    countUp(field, target) {
      if (!target) { this[field] = 0; return; }
      const start = performance.now();
      const dur = 700;
      const step = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        this[field] = Math.round(target * eased);
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    },
  },
};

function truncateStr(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
</script>

<style scoped lang="scss">
.tip-dynamics {
  background: linear-gradient(135deg, #07255B 0%, #1d3a8a 100%);
  border-radius: 24px;
  padding: 24px 28px 22px;
  color: #ffffff;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.2);
  margin: 16px 0;
  width: 100%;
  box-sizing: border-box;
}

/* === HEADER === */
.dyn-header {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;
  padding-bottom: 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.dyn-side { display: flex; flex-direction: column; min-width: 0; }
.dyn-side-home { align-items: flex-end; text-align: right; }
.dyn-side-away { align-items: flex-start; text-align: left; }
.dyn-team-name {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Narrow viewport: stack header vertically, centred. Avoids team-name truncation
   and the awkward 3-column squeeze on mobile. */
@media (max-width: 600px) {
  .dyn-header {
    grid-template-columns: 1fr;
    gap: 10px;
    text-align: center;
  }
  .dyn-side-home,
  .dyn-side-away {
    align-items: center;
    text-align: center;
  }
  .dyn-team-name { font-size: 17px; }
  .dyn-score { font-size: 38px; }
}
.dyn-team-label {
  font-size: 10px;
  letter-spacing: 1.5px;
  opacity: 0.5;
  margin-top: 2px;
  font-weight: 600;
}

.dyn-centre { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.dyn-score {
  display: flex;
  gap: 10px;
  font-size: 44px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.dyn-goal-home { color: #407BFF; }
.dyn-goal-away { color: #FFCF75; }
.dyn-score-sep { color: rgba(255, 255, 255, 0.35); }

.dyn-pills { display: flex; gap: 8px; }
.dyn-pill {
  background: rgba(255, 255, 255, 0.13);
  padding: 4px 10px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 12px;
}
.dyn-pill-minute {
  background: rgba(64, 123, 255, 0.22);
  animation: pulse 2.5s infinite;
}
.dyn-pill-conf { letter-spacing: 1px; }
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.75; }
}

/* === INSIGHT STRIP === */
.dyn-insights {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin: 18px 0 22px;
}
@media (min-width: 768px) {
  .dyn-insights { grid-template-columns: 1fr 1fr; }
}
.dyn-insight {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.dyn-insight-trigger {
  background: rgba(255, 207, 117, 0.13);
  border-color: rgba(255, 207, 117, 0.35);
}
.dyn-insight.is-home {
  background: rgba(64, 123, 255, 0.18);
  border-color: rgba(64, 123, 255, 0.45);
}
.dyn-insight.is-away {
  background: rgba(255, 207, 117, 0.16);
  border-color: rgba(255, 207, 117, 0.45);
}
.dyn-insight-icon { font-size: 22px; line-height: 1; }
.dyn-insight-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1.3px;
  opacity: 0.7;
  margin-bottom: 3px;
  font-weight: 600;
}
.dyn-insight-text { font-weight: 600; font-size: 14px; line-height: 1.35; }

/* === STAT GRID === */
.dyn-stats-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px 24px;
}
@media (min-width: 700px) {
  .dyn-stats-grid { grid-template-columns: 1fr 1fr; }
}
@media (min-width: 1200px) {
  .dyn-stats-grid { grid-template-columns: 1fr 1fr 1fr; }
}

.dyn-stat {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 12px 16px;
}
.dyn-stat-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  min-height: 18px;
}
.dyn-stat-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  opacity: 0.72;
  font-weight: 700;
}
.dyn-stat-winner {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 8px;
  font-weight: 700;
  white-space: nowrap;
}
.dyn-stat-winner.is-home { background: rgba(64, 123, 255, 0.28); color: #cfdfff; }
.dyn-stat-winner.is-away { background: rgba(255, 207, 117, 0.25); color: #ffe2b3; }

/* Split bar: home-value | track | away-value */
.dyn-split {
  display: grid;
  grid-template-columns: 48px 1fr 48px;
  align-items: center;
  gap: 10px;
}
.dyn-split-val {
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.dyn-split-val-home { text-align: right; color: #cfdfff; }
.dyn-split-val-away { text-align: left; color: #ffe2b3; }
.dyn-split-track {
  display: flex;
  height: 10px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 6px;
  overflow: hidden;
}
.dyn-split-fill {
  height: 100%;
  transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
}
.dyn-split-home {
  background: linear-gradient(90deg, #3555FE, #3EA1FC);
  box-shadow: inset 0 0 8px rgba(64, 123, 255, 0.4);
}
.dyn-split-away {
  background: linear-gradient(90deg, #FF9C40, #FFCF75);
  box-shadow: inset 0 0 8px rgba(255, 207, 117, 0.4);
}
</style>
